import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { canUseDOM } from '@ps/web-core-utils';
import { TrackingContext, inputFilled, inputFocused } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useField } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  getIsAvailableOpportunity,
  getIsMultipleOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { useHasPromotionAdded } from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useHasPromotionAdded';

import useCreateEntities from '../../../../../../../../hooks/useCreateEntities';
import { useNumberFromIntl } from '../../../../../../../../hooks/useNumberFromIntl';
import { formatStakeInputValue } from '../../../../../../../../utils/formatStakeInputValue';
import { intlStringToNumber } from '../../../../../../../../utils/intlStringToNumber';
import { getStakeInputRegex } from '../../../../../../../../validation/getStakeInputRegex';
import validateStake from '../../../../../../../../validation/validateStake';

import { TXTWCL_Stake } from './StakeInput.messages.json';
import styles from './StakeInput.scss';

interface StakeInputProps {
  fieldName: string;
  numberOfLines: number;
  minStake: number;
  maxStake: number;
  minLineStake?: number;
  minStakeIncrement: number;
  maxPayout?: number;
  opportunity: SingleOpportunity | MultipleOpportunity;
  isEachWaySelected?: boolean;
  /*
    This is passed here as a prop rather than calculated in validateStake as react final form needs to change the key
    prop in order to revalidate the stake on stake change.
    Using winPotentialReturn would mean the stake field loses focus on every stake change, with this solution it will
    only lose focus when the winPotentialReturn goes from below to above maxPayout or from above to below maxPayout.
  */
  isWinPotentialReturnAboveMaxPayout: boolean;
}

const StakeInput: React.FC<StakeInputProps> = ({
  fieldName,
  numberOfLines,
  minStake,
  maxStake,
  minLineStake,
  minStakeIncrement,
  maxPayout,
  isWinPotentialReturnAboveMaxPayout,
  opportunity
}) => {
  const site = useSelector(select.cms.getSiteId);
  const cursorStartPosition = useSelector(select.betSlip.stakeCursorStartPosition);
  const cursorEndPosition = useSelector(select.betSlip.stakeCursorEndPosition);
  const getNumberFromIntl = useNumberFromIntl();
  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const dispatch = useDispatch<Dispatch>();
  const inputRef = useRef<HTMLInputElement>(null);

  const cursorStartPositionRef = useRef(cursorStartPosition);
  const cursorEndPositionRef = useRef(cursorEndPosition);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    function updateSelectionRange() {
      if (canUseDOM && document.activeElement === input && input) {
        const { selectionStart, selectionEnd } = input;

        dispatch.betSlip.updateStakeCursorPosition({
          selectionStart: selectionStart ?? 0,
          selectionEnd: selectionEnd ?? 0
        });
      }
    }

    input.addEventListener('selectionchange', updateSelectionRange);

    return () => {
      input.removeEventListener('selectionchange', updateSelectionRange);
    };
  }, [dispatch.betSlip]);

  useEffect(() => {
    cursorStartPositionRef.current = cursorStartPosition;
    cursorEndPositionRef.current = cursorEndPosition;
  }, [cursorStartPosition, cursorEndPosition]);

  const {
    input,
    meta: { error, submitting }
  } = useField<number, HTMLInputElement>(`${fieldName}.stakePerLine`, {
    subscription: { value: true, error: true, submitting: true },
    validate: (stake) =>
      isAvailableOpportunity &&
      validateStake({
        minStake,
        maxStake,
        minLineStake,
        minStakeIncrement,
        numberOfLines,
        site,
        maxPayout,
        isWinPotentialReturnAboveMaxPayout,
        stake: getNumberFromIntl(stake)
      })
  });

  useEffect(() => {
    /**
     * As in selection state update we check for current dom active element which is not available in JSDom,
     * we need to disable this selection update as it causes flaky tests due to cursor positioning
     */
    if (process.env.NODE_ENV === 'test') {
      return;
    }

    if (!input.value) {
      return;
    }

    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(
        cursorStartPositionRef.current,
        cursorEndPositionRef.current
      );
    });
  }, [input.value]);

  const isCustomKeyboardEnabled = useSelector(select.betSlip.isCustomKeyboardEnabled);
  const isMobileDevice = useSelector(select.device.isMobile);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const stakeInputDecimalSeparator = useSelector(select.betSlip.stakeInputDecimalSeparator);
  const stakeInputRegex = useMemo(
    () => getStakeInputRegex(stakeInputDecimalSeparator),
    [stakeInputDecimalSeparator]
  );

  const { hasPromotionAdded } = useHasPromotionAdded(fieldName);
  const selectionPromotionPicker = useSelector(select.betSlip.selectedPromotionPicker);
  const { createSingleEntities, createMultipleEntities } = useCreateEntities();

  const tracking = useContext(TrackingContext);
  const trackingLabelFieldName = useTrackingLabel(TXTWCL_Stake);
  const trackingLabelError = useTrackingLabel(error?.translationTag, { 0: error?.value });

  const entities = useMemo(() => {
    if (isAvailableOpportunity) {
      if (getIsMultipleOpportunity(opportunity)) {
        return createMultipleEntities([opportunity]);
      } else {
        return createSingleEntities([opportunity]);
      }
    }

    return [];
  }, [opportunity, createSingleEntities, createMultipleEntities, isAvailableOpportunity]);

  const handleOnFocus = () => {
    if (isMobileDevice) {
      /**
       * When the bet slip is scrollable because there are multiple opportunities,
       * if the user clicks on the stake input for the last opportunity,
       * it will not scroll into view because it is at the bottom of the bet slip.
       * To fix this, we delay the scroll by 1ms which allows the bet slip size increase to apply
       * before it attempts to scroll.
       */
      const scrollAfterCssApplied = debounce(
        () => inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
        1
      );
      scrollAfterCssApplied();
    }

    inputFocused(
      {
        category: tracking.category,
        widget: tracking.widget,
        entities,
        isMobileEmbedded
      },
      {
        fieldName: trackingLabelFieldName,
        fieldType: 'number'
      }
    );

    input.onFocus();
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const inputHasDecimalSeparator = event.currentTarget.value.includes(stakeInputDecimalSeparator);

    if (
      event.key === 'Enter' ||
      (inputHasDecimalSeparator && event.key === stakeInputDecimalSeparator)
    ) {
      event.preventDefault();
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const { inputType, data } = event.nativeEvent as InputEvent;

    if (inputType === 'deleteContentBackward') {
      input.onChange(value);

      const selectionLength = cursorEndPosition - cursorStartPosition;
      const newCursorPosition = selectionLength > 0 ? cursorStartPosition : cursorStartPosition - 1;

      dispatch.betSlip.updateStakeCursorPosition({
        selectionStart: newCursorPosition,
        selectionEnd: newCursorPosition
      });

      return;
    }

    const formattedInputValue = formatStakeInputValue({
      currentValue: value,
      validationRegex: stakeInputRegex,
      decimalSeparator: stakeInputDecimalSeparator
    });

    const isValid = stakeInputRegex.test(formattedInputValue);

    if (isValid) {
      input.onChange(formattedInputValue);

      dispatch.betSlip.updateStakeCursorPosition({
        selectionStart: cursorStartPosition + (data?.length ?? 0),
        selectionEnd: cursorStartPosition + (data?.length ?? 0)
      });
    }
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    inputFilled(
      {
        category: tracking.category,
        widget: tracking.widget,
        entities,
        isMobileEmbedded
      },
      {
        fieldName: trackingLabelFieldName,
        numInput: intlStringToNumber(value),
        fieldType: 'number',
        fieldErrors: trackingLabelError ? [trackingLabelError] : null
      }
    );
    input.onBlur(event);
  };

  return (
    <>
      <label htmlFor={input.name} className={styles.stakeLabel}>
        <Translation {...TXTWCL_Stake} />
      </label>

      <div
        className={classNames(styles.container, {
          [styles.error]: error,
          [styles.disabled]: submitting,
          [styles.promotionApplied]: hasPromotionAdded
        })}
      >
        <input
          {...input}
          type="text"
          ref={inputRef}
          id={input.name}
          className={classNames(styles.input, {
            [styles.promotionAppliedInput]: hasPromotionAdded,
            [styles.unavailableOpportunity]: !isAvailableOpportunity
          })}
          maxLength={9}
          autoComplete={isCustomKeyboardEnabled ? 'off' : undefined}
          inputMode={isCustomKeyboardEnabled ? 'none' : 'decimal'}
          disabled={Boolean(
            submitting || hasPromotionAdded || selectionPromotionPicker || !isAvailableOpportunity
          )}
          onFocus={handleOnFocus}
          onKeyDown={handleOnKeyDown}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
        />

        {numberOfLines > 1 && (
          <span className={styles.numberOfLines} data-testid="number-of-lines">
            x{numberOfLines}
          </span>
        )}
      </div>
    </>
  );
};

const StakeInputWrapper = (props: StakeInputProps) => (
  <StakeInput
    {...props}
    /**
     * Whatever parameters are being used to calculate
     * the validation function need to be combined in such
     * a way that, when they change, this key prop
     * changes, thus re-registering this field (and its
     * validation function) with Final Form.
     */
    key={[
      props.fieldName,
      props.minStake,
      props.maxStake,
      props.minStakeIncrement,
      props.isWinPotentialReturnAboveMaxPayout
    ].join('_')}
  />
);

export default StakeInputWrapper;
